import { useEffect, useState, useMemo } from "react";
import { Plus, BriefcaseBusiness, Trash2, KeyRound, Copy, Check, Building2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";

export default function Clients() {
    const [items, setItems] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", contact: "", email: "", phone: "", notes: "", sla_hours: 24 });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Bulk-assign properties modal
    const [assignClient, setAssignClient] = useState(null); // client object or null
    const [assignSelected, setAssignSelected] = useState(new Set()); // property ids
    const [assignSearch, setAssignSearch] = useState("");
    const [assignSaving, setAssignSaving] = useState(false);

    // Portal access modal
    const [portalOpen, setPortalOpen] = useState(false);
    const [portalClient, setPortalClient] = useState(null);
    const [portalForm, setPortalForm] = useState({ email: "", name: "", password: "" });
    const [portalSaving, setPortalSaving] = useState(false);
    const [portalError, setPortalError] = useState("");
    const [portalResult, setPortalResult] = useState(null);
    const [portalUsers, setPortalUsers] = useState({}); // {client_id: [user]}
    const [copied, setCopied] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([api.get("/clients"), api.get("/properties"), api.get("/client-portal-users")])
            .then(([c, p, u]) => {
                setItems(c.data || []);
                setProperties(p.data || []);
                setPortalUsers(u.data || {});
            })
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            await api.post("/clients", { ...form, sla_hours: Number(form.sla_hours) });
            setOpen(false);
            setForm({ name: "", contact: "", email: "", phone: "", notes: "", sla_hours: 24 });
            load();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar cliente?")) return;
        await api.delete(`/clients/${id}`);
        load();
    };

    const openPortalAccess = (c) => {
        setPortalClient(c);
        const existing = (portalUsers[c.id] || [])[0];
        setPortalForm({
            email: existing?.email || (c.email || "").toLowerCase(),
            name: existing?.name || c.name,
            password: "",
        });
        setPortalResult(null);
        setPortalError("");
        setPortalOpen(true);
    };

    const submitPortal = async (e) => {
        e?.preventDefault?.();
        setPortalSaving(true);
        setPortalError("");
        setPortalResult(null);
        try {
            const payload = {
                email: portalForm.email.trim(),
                name: portalForm.name.trim(),
            };
            if (portalForm.password.trim()) payload.password = portalForm.password.trim();
            const { data } = await api.post(`/clients/${portalClient.id}/portal-access`, payload);
            setPortalResult(data);
            // refresh list
            api.get(`/clients/${portalClient.id}/portal-users`)
                .then((r) => setPortalUsers((prev) => ({ ...prev, [portalClient.id]: r.data || [] })))
                .catch(() => {});
        } catch (err) {
            setPortalError(formatApiError(err));
        } finally {
            setPortalSaving(false);
        }
    };

    const revokeAccess = async (cid, uid) => {
        if (!window.confirm("¿Revocar el acceso de este usuario al portal?")) return;
        await api.delete(`/clients/${cid}/portal-access/${uid}`);
        const r = await api.get(`/clients/${cid}/portal-users`);
        setPortalUsers((prev) => ({ ...prev, [cid]: r.data || [] }));
    };

    const copyCredentials = (result) => {
        const text = `Portal ZENTRA — ${result.client_name}\nURL: ${window.location.origin}/login\nEmail: ${result.email}\nContraseña: ${result.password}`;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const countProps = (clientId) => properties.filter((p) => p.client_id === clientId).length;

    const openAssign = (client) => {
        const currentlyAssignedIds = new Set(
            properties.filter((p) => p.client_id === client.id).map((p) => p.id),
        );
        setAssignClient(client);
        setAssignSelected(currentlyAssignedIds);
        setAssignSearch("");
    };

    const closeAssign = () => {
        setAssignClient(null);
        setAssignSelected(new Set());
        setAssignSearch("");
    };

    const toggleAssign = (pid) => {
        setAssignSelected((prev) => {
            const next = new Set(prev);
            if (next.has(pid)) next.delete(pid);
            else next.add(pid);
            return next;
        });
    };

    const saveAssign = async () => {
        if (!assignClient) return;
        setAssignSaving(true);
        try {
            const targetIds = Array.from(assignSelected);
            const currentlyAssignedIds = properties
                .filter((p) => p.client_id === assignClient.id)
                .map((p) => p.id);
            const toAssign = targetIds.filter((id) => !currentlyAssignedIds.includes(id));
            const toUnassign = currentlyAssignedIds.filter((id) => !targetIds.includes(id));
            if (toAssign.length > 0) {
                await api.post("/properties/bulk-assign-client", {
                    client_id: assignClient.id,
                    property_ids: toAssign,
                });
            }
            if (toUnassign.length > 0) {
                await api.post("/properties/bulk-assign-client", {
                    client_id: null,
                    property_ids: toUnassign,
                });
            }
            closeAssign();
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setAssignSaving(false);
        }
    };

    const assignFilteredProps = useMemo(() => {
        const s = assignSearch.trim().toLowerCase();
        if (!s) return properties;
        return properties.filter(
            (p) => (p.code || "").toLowerCase().includes(s) || (p.name || "").toLowerCase().includes(s),
        );
    }, [properties, assignSearch]);

    return (
        <div className="space-y-6" data-testid="clients-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Cartera comercial
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">Clientes</h2>
                    <p className="text-sm text-zentra-muted mt-1">{items.length} cuentas activas</p>
                </div>
                <button className="zentra-btn-primary" onClick={() => setOpen(true)} data-testid="new-client-button">
                    <Plus size={16} /> Nuevo cliente
                </button>
            </div>

            {loading ? (
                <div className="zentra-skeleton h-72" />
            ) : items.length === 0 ? (
                <EmptyState icon={BriefcaseBusiness} title="Sin clientes" description="Añade tu primera cuenta cliente." />
            ) : (
                <div className="zentra-card-static overflow-hidden">
                    <table className="zentra-table" data-testid="clients-table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Contacto</th>
                                <th>Propiedades</th>
                                <th>SLA</th>
                                <th>Portal</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => {
                                const users = portalUsers[c.id] || [];
                                return (
                                    <tr key={c.id} data-testid={`client-row-${c.id}`}>
                                        <td>
                                            <p className="font-medium text-zentra-ink">{c.name}</p>
                                            {c.notes && <p className="text-[11px] text-zentra-muted">{c.notes}</p>}
                                        </td>
                                        <td className="text-sm">
                                            <p>{c.contact}</p>
                                            <p className="text-xs text-zentra-muted">{c.email}</p>
                                            <p className="text-xs text-zentra-muted font-mono-zentra">{c.phone}</p>
                                        </td>
                                        <td className="font-display font-semibold">{countProps(c.id)}</td>
                                        <td>
                                            <span className="zentra-chip bg-zentra-bg text-zentra-ink border-zentra-border">
                                                {c.sla_hours}h
                                            </span>
                                        </td>
                                        <td>
                                            {users.length > 0 ? (
                                                <div className="space-y-1">
                                                    {users.map((u) => (
                                                        <div key={u.id} className="flex items-center gap-2">
                                                            <span className="text-xs font-mono-zentra text-zentra-ink">{u.email}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => revokeAccess(c.id, u.id)}
                                                                className="text-[10px] text-red-700 hover:underline"
                                                                data-testid={`revoke-portal-${u.id}`}
                                                            >
                                                                Revocar
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-[11px] text-zentra-muted">Sin acceso</span>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => openAssign(c)}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zentra-border bg-white text-xs font-semibold text-zentra-ink hover:border-zentra-olive"
                                                    data-testid={`assign-properties-${c.id}`}
                                                >
                                                    <Building2 size={12} />
                                                    Asignar propiedades
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openPortalAccess(c)}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zentra-border bg-white text-xs font-semibold text-zentra-ink hover:border-zentra-olive"
                                                    data-testid={`portal-access-${c.id}`}
                                                >
                                                    <KeyRound size={12} />
                                                    {users.length > 0 ? "Cambiar acceso" : "Crear acceso"}
                                                </button>
                                                <button
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50"
                                                    onClick={() => remove(c.id)}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Nuevo cliente"
                wide
                testId="client-modal"
                footer={
                    <>
                        <button className="zentra-btn-ghost" onClick={() => setOpen(false)}>
                            Cancelar
                        </button>
                        <button type="submit" form="client-form" className="zentra-btn-primary" disabled={saving} data-testid="save-client">
                            {saving ? "Guardando…" : "Crear"}
                        </button>
                    </>
                }
            >
                <form id="client-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className="zentra-label">Nombre / razón social</label>
                        <input className="zentra-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" />
                    </div>
                    <div>
                        <label className="zentra-label">Persona de contacto</label>
                        <input className="zentra-input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">SLA (horas)</label>
                        <input type="number" className="zentra-input" value={form.sla_hours} onChange={(e) => setForm({ ...form, sla_hours: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">Email</label>
                        <input className="zentra-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">Teléfono</label>
                        <input className="zentra-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Notas</label>
                        <textarea className="zentra-textarea min-h-[70px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                    {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
                </form>
            </Modal>

            {/* Bulk-assign properties modal */}
            <Modal
                open={!!assignClient}
                onClose={closeAssign}
                title={assignClient ? `Propiedades de ${assignClient.name}` : ""}
                subtitle="Marca las propiedades que pertenecen a este cliente. Se aplican todas a la vez al guardar."
                wide
                testId="assign-props-modal"
                footer={
                    <>
                        <button className="zentra-btn-ghost" onClick={closeAssign} data-testid="assign-props-cancel">
                            Cancelar
                        </button>
                        <button
                            className="zentra-btn-primary"
                            disabled={assignSaving}
                            onClick={saveAssign}
                            data-testid="assign-props-save"
                        >
                            {assignSaving ? "Guardando…" : `Asignar ${assignSelected.size} propiedades`}
                        </button>
                    </>
                }
            >
                <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[240px]">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zentra-muted" />
                            <input
                                className="zentra-input pl-9"
                                placeholder="Buscar por código o nombre…"
                                value={assignSearch}
                                onChange={(e) => setAssignSearch(e.target.value)}
                                data-testid="assign-props-search"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() =>
                                setAssignSelected(new Set(assignFilteredProps.map((p) => p.id)))
                            }
                            className="zentra-btn-ghost text-xs"
                            data-testid="assign-props-select-all"
                        >
                            Seleccionar todos ({assignFilteredProps.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setAssignSelected(new Set())}
                            className="zentra-btn-ghost text-xs"
                            data-testid="assign-props-clear"
                        >
                            Quitar todos
                        </button>
                    </div>
                    <p className="text-[11px] text-zentra-muted">
                        Seleccionadas: <strong className="text-zentra-ink">{assignSelected.size}</strong> de {properties.length}
                    </p>
                    <div className="border border-zentra-border rounded-md max-h-[420px] overflow-y-auto divide-y divide-zentra-border">
                        {assignFilteredProps.map((p) => {
                            const checked = assignSelected.has(p.id);
                            const belongsToAnother =
                                p.client_id && assignClient && p.client_id !== assignClient.id;
                            const otherClient = belongsToAnother
                                ? items.find((c) => c.id === p.client_id)
                                : null;
                            return (
                                <label
                                    key={p.id}
                                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-zentra-bg/50 ${
                                        checked ? "bg-zentra-accent/5" : ""
                                    }`}
                                    data-testid={`assign-prop-row-${p.id}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleAssign(p.id)}
                                        className="w-4 h-4 accent-zentra-primary"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-mono-zentra text-xs text-zentra-muted">{p.code}</p>
                                        <p className="text-sm text-zentra-ink truncate">{p.name}</p>
                                    </div>
                                    {belongsToAnother && (
                                        <span
                                            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-100 text-amber-900 border-amber-300"
                                            title={`Actualmente asignada a ${otherClient?.name || "otro cliente"}`}
                                        >
                                            {otherClient?.name?.slice(0, 14) || "Otro cliente"}
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                        {assignFilteredProps.length === 0 && (
                            <p className="px-3 py-6 text-center text-sm text-zentra-muted">Sin resultados.</p>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Portal access modal */}
            <Modal
                open={portalOpen}
                onClose={() => setPortalOpen(false)}
                title={`Acceso al portal · ${portalClient?.name || ""}`}
                subtitle="Crea o regenera la contraseña del usuario cliente. Las credenciales solo se muestran una vez al generarlas."
                wide
                testId="portal-access-modal"
                footer={
                    portalResult ? (
                        <button className="zentra-btn-primary" onClick={() => setPortalOpen(false)} data-testid="portal-access-close">
                            Hecho
                        </button>
                    ) : (
                        <>
                            <button className="zentra-btn-ghost" onClick={() => setPortalOpen(false)}>
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                form="portal-access-form"
                                className="zentra-btn-primary"
                                disabled={portalSaving}
                                data-testid="portal-access-save"
                            >
                                {portalSaving ? "Guardando…" : "Generar acceso"}
                            </button>
                        </>
                    )
                }
            >
                {portalResult ? (
                    <div className="space-y-4" data-testid="portal-access-result">
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                            <p className="text-sm font-semibold text-emerald-900 mb-3">
                                ✓ Acceso {portalResult.action === "created" ? "creado" : "actualizado"} correctamente.
                            </p>
                            <div className="font-mono-zentra text-sm space-y-1.5">
                                <div><span className="text-zentra-muted text-xs uppercase tracking-wider mr-2">URL:</span>{window.location.origin}/login</div>
                                <div><span className="text-zentra-muted text-xs uppercase tracking-wider mr-2">Email:</span>{portalResult.email}</div>
                                <div><span className="text-zentra-muted text-xs uppercase tracking-wider mr-2">Pass:</span>{portalResult.password}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => copyCredentials(portalResult)}
                            className="zentra-btn-ghost"
                            data-testid="portal-access-copy"
                        >
                            {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar credenciales</>}
                        </button>
                        <p className="text-xs text-zentra-muted">
                            Comparte estas credenciales con tu cliente por un canal seguro. No se podrán volver a mostrar.
                        </p>
                    </div>
                ) : (
                    <form id="portal-access-form" onSubmit={submitPortal} className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="zentra-label">Nombre del usuario</label>
                            <input
                                className="zentra-input"
                                required
                                value={portalForm.name}
                                onChange={(e) => setPortalForm({ ...portalForm, name: e.target.value })}
                                data-testid="portal-access-name"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="zentra-label">Email de acceso</label>
                            <input
                                type="email"
                                className="zentra-input"
                                required
                                value={portalForm.email}
                                onChange={(e) => setPortalForm({ ...portalForm, email: e.target.value })}
                                data-testid="portal-access-email"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="zentra-label">Contraseña (opcional · vacío para auto)</label>
                            <input
                                className="zentra-input"
                                value={portalForm.password}
                                onChange={(e) => setPortalForm({ ...portalForm, password: e.target.value })}
                                placeholder="Mínimo 4 caracteres"
                                data-testid="portal-access-password"
                            />
                            <p className="text-[11px] text-zentra-muted mt-1.5">
                                Si lo dejas en blanco, ZENTRA generará una contraseña aleatoria segura.
                            </p>
                        </div>
                        {portalError && (
                            <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                                {portalError}
                            </div>
                        )}
                    </form>
                )}
            </Modal>
        </div>
    );
}
